import React, { useContext, useEffect, useMemo, useState } from "react";
import { GoogleMapsContext, InfoWindow } from "@vis.gl/react-google-maps";
import { useAtom } from "jotai";
import {
    dateRangeAtom,
    filteredProviderLocationsAtom,
    openedInfoWindowTypeAtom,
    canShowTravelZoneInfoWindowAtom,
    showActiveTravelZonesAtom,
    showExpiredTravelZonesAtom
} from "./analysisAtoms";
import { endOfDay, format, startOfDay } from "date-fns";
import { Box, Typography, useTheme } from "@mui/material";
import ScheduleCard from "./ScheduleCard";
import {
    INFO_WINDOW_NONE,
    INFO_WINDOW_TRAVELZONE
} from "../../utils/Constants";
const GRAY = "#808080";
const Polygon = ({
    paths,
    strokeColor = "#008000",
    fillColor = "#008000",
    fillOpacity = 0.15,
    onHover = () => {},
    onClick = () => {},
    providerName,
    scheduleInDateRange,
    providerLocInfo
}) => {
    const map = useContext(GoogleMapsContext)?.map;
    useEffect(() => {
        const polygon = new window.google.maps.Polygon({
            paths,
            strokeColor: scheduleInDateRange ? strokeColor : GRAY,
            strokeOpacity: 0.5,
            strokeWeight: 1,
            fillColor: scheduleInDateRange ? fillColor : GRAY,
            fillOpacity
        });
        polygon.setMap(map);
        polygon.addListener("mouseover", () => onHover(providerName, paths[0]));
        polygon.addListener("mouseout", () => onHover(null, null));
        polygon.addListener("click", () => onClick(providerLocInfo, paths[0]));
        return () => polygon.setMap(null);
    }, [paths, strokeColor, fillColor, fillOpacity, scheduleInDateRange]);
    return null;
};

export default function TravelRegionPolygon() {
    const map = useContext(GoogleMapsContext)?.map;
    const [dateRange] = useAtom(dateRangeAtom);
    const [filteredProviderLocations] = useAtom(filteredProviderLocationsAtom);
    const [showActiveTravelZones] = useAtom(showActiveTravelZonesAtom);
    const [showExpiredTravelZones] = useAtom(showExpiredTravelZonesAtom);
    const [, setOpenedInofWindowType] = useAtom(openedInfoWindowTypeAtom);
    const [canShowTravelZoneInfoWindow] = useAtom(
        canShowTravelZoneInfoWindowAtom
    );
    const [infoPosition, setInfoPosition] = useState(null);
    const [clickedProviderInfo, setClickedProviderInfo] = useState();
    const theme = useTheme();

    const dateRangeStart = startOfDay(dateRange.start);

    function hasCurrentOrFutureSB(sbs) {
        let result = false;
        for (let sb of sbs) {
            const endDate = endOfDay(new Date(sb.endDate));
            result = endDate >= dateRangeStart;
            if (result) break;
        }
        return result;
    }
    const bounds = useMemo(() => {
        if (
            !filteredProviderLocations ||
            filteredProviderLocations.length === 0
        )
            return null;

        const latLngBounds = new window.google.maps.LatLngBounds();

        filteredProviderLocations.forEach((providerLoc) => {
            providerLoc.travelregions.forEach((zone) =>
                zone.forEach((coord) => {
                    latLngBounds.extend(
                        new window.google.maps.LatLng(coord.lat, coord.lng)
                    );
                })
            );
        });

        return latLngBounds;
    }, [filteredProviderLocations]);

    useEffect(() => {
        if (map && bounds) {
            map.fitBounds(bounds);
        }
    }, [bounds, map]);
    function handleClick(plInfo, location) {
        if (plInfo && location) {
            if (clickedProviderInfo && plInfo.id === clickedProviderInfo.id) {
                // toggle
                setOpenedInofWindowType(INFO_WINDOW_NONE);
                setClickedProviderInfo(null);
                setInfoPosition(null);
            } else {
                setOpenedInofWindowType(INFO_WINDOW_TRAVELZONE);
                setClickedProviderInfo(plInfo);
                setInfoPosition(location);
            }
        } else {
            setInfoPosition(null);
            setOpenedInofWindowType(INFO_WINDOW_NONE);
        }
    }

    return (
        <>
            {(showActiveTravelZones || showExpiredTravelZones) &&
                filteredProviderLocations?.map((providerLoc) =>
                    providerLoc.travelregions.map((zone, index) => {
                        const scheduleIsInDateRange = hasCurrentOrFutureSB(
                            providerLoc.SBs
                        );
                        return !scheduleIsInDateRange &&
                            !showExpiredTravelZones ? (
                            <></>
                        ) : scheduleIsInDateRange && !showActiveTravelZones ? (
                            <></>
                        ) : (
                            <Polygon
                                key={`${providerLoc?.id}${index}`}
                                paths={zone}
                                providerName={`${providerLoc.firstname} ${providerLoc.lastname}`}
                                providerLocInfo={providerLoc}
                                location={{
                                    lat: providerLoc.latitude,
                                    lng: providerLoc.longitude
                                }}
                                onClick={handleClick}
                                scheduleInDateRange={hasCurrentOrFutureSB(
                                    providerLoc.SBs
                                )}
                            />
                        );
                    })
                )}
            {clickedProviderInfo &&
                infoPosition &&
                canShowTravelZoneInfoWindow && (
                    <InfoWindow
                        position={infoPosition}
                        onCloseClick={() => {
                            setClickedProviderInfo(null);
                            setOpenedInofWindowType(INFO_WINDOW_NONE);
                        }}
                        headerContent={
                            <Typography variant="h6">{`${clickedProviderInfo?.firstname} ${clickedProviderInfo?.lastname}`}</Typography>
                        }
                    >
                        <Typography variant="body2" color="text.secondary">
                            <strong>Travel zone</strong>
                        </Typography>
                        <Box
                            sx={{
                                width: 300,
                                borderRadius: 1,
                                padding: 1,
                                marginBottom: 1,
                                bgcolor: theme.palette.primary.light
                            }}
                        >
                            {clickedProviderInfo?.updatedAt && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    <strong>Last updated on:</strong>{" "}
                                    {format(
                                        new Date(
                                            clickedProviderInfo?.updatedAt
                                        ),
                                        "MMM dd, yyyy – h:mm a"
                                    )}
                                </Typography>
                            )}
                            {clickedProviderInfo?.maxtraveltype ===
                                "MAX_DISTANCE" && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    <strong>Radius:</strong>{" "}
                                    {clickedProviderInfo?.traveldistance}{" "}
                                    {clickedProviderInfo?.traveldistanceunit}
                                </Typography>
                            )}
                        </Box>
                        {clickedProviderInfo?.SBs?.length && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>Schedule</strong>
                            </Typography>
                        )}
                        {clickedProviderInfo?.SBs.map((sb) => (
                            <ScheduleCard key={sb.scheduleinfo} schedule={sb} />
                        ))}
                    </InfoWindow>
                )}
        </>
    );
}
